import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { 
  Button, 
  Card, 
  Typography, 
  Form, 
  Select, 
  Space, 
  message, 
  Table, 
  Tag, 
  Input,
  Row, 
  Col,
  Divider
} from "antd";
import { 
  SearchOutlined, 
  ReloadOutlined, 
  DownloadOutlined,
  CopyOutlined,
  DeleteOutlined,
  ClearOutlined
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import { useFetch } from "../../../Utils/useFetch";
import { FETCH_ALL_DEPT_API, FETCH_ALL_COLOR_API } from "../../../Constants/Apis";
import { getArticlesByDepartment } from "../../../Services/ArticleService";

const { Title, Text } = Typography;
const { Option } = Select;

const GenerateBarcodePage = ({ history, userData }) => {
  const [form] = Form.useForm();
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [minSize, setMinSize] = useState(null);
  const [maxSize, setMaxSize] = useState(null);
  const [generatedBarcodes, setGeneratedBarcodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  // Fetch departments
  const { fetchedData: deptData } = useFetch(FETCH_ALL_DEPT_API, {});
  const departments = deptData?.data || [];

  // Fetch colors
  const { fetchedData: colorData } = useFetch(FETCH_ALL_COLOR_API, {});
  const colors = colorData?.data || [];

  // Fetch articles when department is selected
  const [articles, setArticles] = useState([]);
  const [articleLoading, setArticleLoading] = useState(false);

  // Fetch articles by department
  useEffect(() => {
    const fetchArticles = async () => {
      if (!selectedDepartment) {
        setArticles([]);
        return;
      }

      setArticleLoading(true);
      try {
        const response = await getArticlesByDepartment(selectedDepartment);
        if (response?.data?.data) {
          setArticles(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching articles:", error);
        message.error("Failed to fetch articles");
      } finally {
        setArticleLoading(false);
      }
    };

    fetchArticles();
  }, [selectedDepartment]);

  // Get available colors for selected article
  const getAvailableColors = (article) => {
    if (!article) return colors;

    // Check if article has any prices with specific colors (non-null colorId)
    const hasSpecificColorPrices = article.prices?.some(price => 
      price.deptId === selectedDepartment && price.colorId !== null
    );

    if (hasSpecificColorPrices) {
      // If article has specific color prices, only show colors that have prices for this department
      const availableColorIds = article.prices
        ?.filter(price => price.deptId === selectedDepartment && price.colorId !== null)
        ?.map(price => price.colorId) || [];
      
      return colors.filter(color => availableColorIds.includes(color._id));
    } else {
      // If all prices have null colorId, show all colors from the article's colorList
      return colors.filter(color => article.colorList?.includes(color._id));
    }
  };

  const availableColors = getAvailableColors(selectedArticle);

  const handleDepartmentChange = (value) => {
    setSelectedDepartment(value);
    setSelectedArticle(null);
    setSelectedColor(null);
    setMinSize(null);
    setMaxSize(null);
    setGeneratedBarcodes([]);
    form.resetFields(['article', 'color', 'minSize', 'maxSize']);
  };

  const handleArticleChange = (value) => {
    const article = articles.find(a => a._id === value);
    setSelectedArticle(article);
    setSelectedColor(null);
    setMinSize(article?.minSize || null);
    setMaxSize(article?.maxSize || null);
    form.resetFields(['color']);
    form.setFieldsValue({
      minSize: article?.minSize || null,
      maxSize: article?.maxSize || null
    });
  };

  const handleColorChange = (value) => {
    setSelectedColor(value);
  };

  const handleSizeChange = (field, value) => {
    if (field === 'minSize') {
      setMinSize(value);
      // Ensure maxSize is not less than minSize
      if (maxSize !== null && value > maxSize) {
        setMaxSize(value);
        form.setFieldsValue({ maxSize: value });
      }
    } else if (field === 'maxSize') {
      setMaxSize(value);
      // Ensure minSize is not greater than maxSize
      if (minSize !== null && value < minSize) {
        setMinSize(value);
        form.setFieldsValue({ minSize: value });
      }
    }
  };

  const generateBarcodes = async () => {
    if (!selectedArticle || !selectedColor || minSize === null || maxSize === null) {
      message.error("Please select article, color, and size range");
      return;
    }

    if (minSize > maxSize) {
      message.error("Minimum size cannot be greater than maximum size");
      return;
    }

    setLoading(true);
    try {
      const color = colors.find(c => c._id === selectedColor);
      
      // Get department price for this article
      const deptPrice = selectedArticle.deptPrice?.harga_internal || selectedArticle.hpp || 0;
      
      const newBarcodes = [];
      
      // Generate barcodes for each size from minSize to maxSize
      for (let size = minSize; size <= maxSize; size++) {
        const sizeStr = String(size).padStart(2, '0');
        const colorCode = (color?.code || '').padStart(3, '0');
        const barcode = `${selectedArticle.preBarcode || ''}${colorCode}${sizeStr}`;
        
        newBarcodes.push({
          _id: `${selectedArticle._id}_${selectedColor}_${size}_${Date.now()}`,
          name: selectedArticle.name,
          brand: selectedArticle.brand,
          subcategory: selectedArticle.subcategory,
          division: selectedArticle.division,
          type: selectedArticle.type,
          hpp: selectedArticle.hpp,
          deptPrice: deptPrice,
          colorName: color?.name || 'No Color',
          size: sizeStr,
          barcode: barcode,
          quantity: 1,
          deptName: departments.find(d => d._id === selectedDepartment)?.name || 'Unknown'
        });
      }
      
      // Add new barcodes to existing ones
      setGeneratedBarcodes(prev => [...prev, ...newBarcodes]);
      message.success(`Generated ${newBarcodes.length} barcodes successfully!`);
    } catch (error) {
      console.error("Error generating barcodes:", error);
      message.error("Failed to generate barcodes");
    } finally {
      setLoading(false);
    }
  };

  const deleteBarcode = (barcodeId) => {
    setGeneratedBarcodes(prev => prev.filter(barcode => barcode._id !== barcodeId));
    message.success("Barcode deleted successfully!");
  };

  const clearAllBarcodes = () => {
    setGeneratedBarcodes([]);
    message.success("All barcodes cleared!");
  };

  const updateQuantity = (barcodeId, newQuantity) => {
    if (newQuantity < 1 || !Number.isInteger(newQuantity)) {
      message.error("Quantity must be a positive integer");
      return;
    }
    
    setGeneratedBarcodes(prev => 
      prev.map(barcode => 
        barcode._id === barcodeId 
          ? { ...barcode, quantity: newQuantity }
          : barcode
      )
    );
  };

  const handleDownloadExcel = async () => {
    if (generatedBarcodes.length === 0) {
      message.warning("No barcodes to download");
      return;
    }

    try {
      setDownloadLoading(true);
      message.loading("Preparing Excel file...", 0);

      // Prepare data for Excel export
      const excelData = generatedBarcodes.map((item, index) => ({
        "Kode Barcode": item.barcode || "",
        "Nama Artikel": item.name || "",
        "Warna & Size": `${item.colorName || "No Color"} ${item.size || ""}`,
        Subkategori: item.subcategory || "",
        Harga: `Rp. ${(item.deptPrice || 0).toLocaleString()}`,
        Quantity: item.quantity || 1,
      }));

      // Create a new workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Set column widths for better readability
      const columnWidths = [
        { wch: 20 },  // Kode Barcode
        { wch: 30 },  // Nama Artikel
        { wch: 15 },  // Warna & Size
        { wch: 15 },  // Subkategori
        { wch: 15 },  // Harga (with Rp. format)
        { wch: 10 },  // Quantity
      ];
      worksheet['!cols'] = columnWidths;

      // Add the worksheet to the workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, "Generated Barcodes");

      // Generate Excel file buffer
      const excelBuffer = XLSX.write(workbook, { 
        bookType: 'xlsx', 
        type: 'array' 
      });

      // Create and download file
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        "Barcode Harga.xlsx"
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.destroy();
      message.success(`Excel file downloaded successfully! (${generatedBarcodes.length} records)`);
    } catch (error) {
      console.error("Error downloading Excel file:", error);
      message.destroy();
      message.error("Failed to download Excel file");
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleCopyBarcode = (barcode) => {
    navigator.clipboard.writeText(barcode).then(() => {
      message.success("Barcode copied to clipboard!");
    }).catch(() => {
      message.error("Failed to copy barcode");
    });
  };

  const columns = [
    {
      title: "Kode Barcode",
      dataIndex: "barcode",
      key: "barcode",
      width: 200,
      fixed: "left",
      render: (text) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "4px 8px",
            border: "1px solid #d9d9d9",
            borderRadius: "4px",
            backgroundColor: "#fafafa",
          }}
        >
          <Text code style={{ fontSize: "12px", flex: 1 }}>
            {text}
          </Text>
          <CopyOutlined
            style={{
              cursor: "pointer",
              color: "#1890ff",
              fontSize: "12px",
            }}
            onClick={() => handleCopyBarcode(text)}
            title="Copy barcode"
          />
        </div>
      ),
    },
    {
      title: "Nama Artikel",
      dataIndex: "name",
      key: "name",
      width: 250,
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Warna & Size",
      key: "colorSize",
      width: 150,
      render: (_, record) => (
        <Text>
          {record.colorName} {record.size}
        </Text>
      ),
    },
    {
      title: "Subkategori",
      dataIndex: "subcategory",
      key: "subcategory",
      width: 150,
    },
    {
      title: "Harga",
      dataIndex: "deptPrice",
      key: "deptPrice",
      width: 120,
      render: (value) => (
        <Text strong style={{ color: "#52c41a" }}>
          Rp. {value?.toLocaleString() || 0}
        </Text>
      ),
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      width: 100,
      render: (value, record) => (
        <Input
          type="number"
          min="1"
          value={value}
          onChange={(e) => {
            const newQuantity = parseInt(e.target.value);
            if (!isNaN(newQuantity)) {
              updateQuantity(record._id, newQuantity);
            }
          }}
          onBlur={(e) => {
            const newQuantity = parseInt(e.target.value);
            if (isNaN(newQuantity) || newQuantity < 1) {
              updateQuantity(record._id, 1);
            }
          }}
          style={{ width: "80px" }}
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => deleteBarcode(record._id)}
          size="small"
          title="Delete barcode"
        />
      ),
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Card>
        <div style={{ marginBottom: "24px" }}>
          <Title level={2} style={{ margin: 0, marginBottom: "8px" }}>
            Generate Barcode
          </Title>
          <Text type="secondary">
            Generate barcodes for articles by selecting department, article, and color
          </Text>
        </div>

        <Form
          form={form}
          layout="vertical"
          style={{ marginBottom: "24px" }}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Department"
                name="department"
                rules={[{ required: true, message: "Please select a department" }]}
              >
                <Select
                  placeholder="Select department"
                  onChange={handleDepartmentChange}
                  loading={!departments.length}
                  showSearch
                  filterOption={(input, option) => {
                    const department = departments.find(d => d._id === option.value);
                    if (!department) return false;
                    return department.name.toLowerCase().includes(input.toLowerCase());
                  }}
                >
                  {departments.map((dept) => (
                    <Option key={dept._id} value={dept._id}>
                      {dept.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label="Article"
                name="article"
                rules={[{ required: true, message: "Please select an article" }]}
              >
                <Select
                  placeholder="Select article"
                  onChange={handleArticleChange}
                  loading={articleLoading}
                  disabled={!selectedDepartment}
                  showSearch
                  filterOption={(input, option) => {
                    const article = articles.find(a => a._id === option.value);
                    if (!article) return false;
                    const searchText = `${article.name} ${article.brand}`.toLowerCase();
                    return searchText.includes(input.toLowerCase());
                  }}
                >
                  {articles.map((article) => (
                    <Option key={article._id} value={article._id}>
                      {article.name} ({article.brand})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label="Color"
                name="color"
                rules={[{ required: true, message: "Please select a color" }]}
              >
                <Select
                  placeholder="Select color"
                  onChange={handleColorChange}
                  disabled={!selectedArticle}
                  showSearch
                  filterOption={(input, option) => {
                    const color = availableColors.find(c => c._id === option.value);
                    if (!color) return false;
                    return color.name.toLowerCase().includes(input.toLowerCase());
                  }}
                >
                  {availableColors.map((color) => (
                    <Option key={color._id} value={color._id}>
                      {color.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {selectedArticle && (
            <Row gutter={16} style={{ marginTop: "16px" }}>
              <Col span={8}>
                <Form.Item
                  label="Min Size"
                  name="minSize"
                  rules={[{ required: true, message: "Please enter minimum size" }]}
                >
                  <Input
                    type="number"
                    placeholder="Min size"
                    min={selectedArticle.minSize || 0}
                    max={selectedArticle.maxSize || 99}
                    value={minSize}
                    onChange={(e) => handleSizeChange('minSize', parseInt(e.target.value) || null)}
                    disabled={!selectedArticle}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Max Size"
                  name="maxSize"
                  rules={[{ required: true, message: "Please enter maximum size" }]}
                >
                  <Input
                    type="number"
                    placeholder="Max size"
                    min={selectedArticle.minSize || 0}
                    max={selectedArticle.maxSize || 99}
                    value={maxSize}
                    onChange={(e) => handleSizeChange('maxSize', parseInt(e.target.value) || null)}
                    disabled={!selectedArticle}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <div style={{ paddingTop: "30px" }}>
                  <Text type="secondary">
                    Range: {selectedArticle.minSize || 0} - {selectedArticle.maxSize || 99}
                  </Text>
                </div>
              </Col>
            </Row>
          )}

          {selectedArticle && (
            <Row gutter={16} style={{ marginTop: "16px" }}>
              <Col span={8}>
                <Text strong>Available Colors: </Text>
                <Text>{availableColors.length}</Text>
              </Col>
              <Col span={8}>
                <Text strong>Dept Price: </Text>
                <Text style={{ color: "#52c41a", fontWeight: "bold" }}>
                  Rp. {(selectedArticle.deptPrice?.harga_internal || selectedArticle.hpp || 0).toLocaleString()}
                </Text>
              </Col>
              <Col span={8}>
                <Text strong>Selected Range: </Text>
                <Text>
                  {minSize !== null && maxSize !== null ? `${minSize} - ${maxSize}` : 'Not selected'}
                </Text>
              </Col>
            </Row>
          )}

          <div style={{ marginTop: "24px", textAlign: "center" }}>
            <Button
              type="primary"
              size="large"
              onClick={generateBarcodes}
              loading={loading}
              disabled={!selectedArticle || !selectedColor || minSize === null || maxSize === null}
            >
              {generatedBarcodes.length > 0 ? 'Add More Barcodes' : 'Generate Barcodes'}
            </Button>
          </div>
        </Form>

        {generatedBarcodes.length > 0 && (
          <>
            <Divider />
            <div style={{ marginBottom: "16px" }}>
              <Space>
                <Text strong>Generated Barcodes: {generatedBarcodes.length}</Text>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={handleDownloadExcel}
                  loading={downloadLoading}
                >
                  Download Excel
                </Button>
                <Button
                  icon={<ClearOutlined />}
                  onClick={clearAllBarcodes}
                  danger
                >
                  Clear All
                </Button>
              </Space>
            </div>

            <Table
              columns={columns}
              dataSource={generatedBarcodes}
              loading={loading}
              rowKey="_id"
              scroll={{ x: 1000, y: 400 }}
              size="small"
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
                pageSizeOptions: ["10", "20", "50", "100"],
              }}
            />
          </>
        )}
      </Card>
    </div>
  );
};

GenerateBarcodePage.propTypes = {
  history: PropTypes.object,
  userData: PropTypes.object,
};

export default GenerateBarcodePage;
